import PaginatedList from "#root/components/paginated-list";
import { AccountDetailItemSchema, AccountListItemSchema } from "#root/schema/account";
import RemoteCollection, { type ListQuery } from "#root/utility/remote-collection";
import type { ReactNode } from "react";
import { Fragment, useMemo } from "react";
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PageLayout from "./layout";

const title = (
  <Fragment>
    Accounts{" "}
    <Link className="button" to="/admin/account/new">
      New Account
    </Link>
  </Fragment>
);

class AccountCollection extends RemoteCollection<typeof AccountListItemSchema, typeof AccountDetailItemSchema> {
  lastUsage: string | undefined;

  constructor(type: string) {
    super("/api/account", AccountListItemSchema, AccountDetailItemSchema);
    const cutoff = new Date(Date.now() - 86_400_000 * 7).toISOString().slice(0, 10);

    switch (type) {
      case "all":
        break;
      case "inactive":
        this.lastUsage = `< ${cutoff}`;
        break;
      default:
        this.lastUsage = `>=${cutoff}`;
        break;
    }
  }

  list(options: ListQuery | undefined) {
    return super.list({ lastUsage: this.lastUsage, ...options });
  }
}

export default function AccountsPage({ type }): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q");

  const filteredCollection = useMemo(() => new AccountCollection(type), [type]);

  if (type && q) {
    navigate({ pathname: "/admin/account", search: location.search });
  }

  return (
    <PageLayout title="Admin - Accounts">
      <section className="p-4">
        <h1>Manage Accounts</h1>
        <p>View and manage CloudQuote accounts</p>

        <PaginatedList title={title} collection={filteredCollection}>
          {({ rows }) => (
            <Fragment>
              {q ? null : (
                <div className="tabbed-navigation">
                  <NavLink end to="/admin/account">
                    Active Accounts (Last 7 Days)
                  </NavLink>
                  <NavLink end to="/admin/account/byType/inactive">
                    Inactive Accounts
                  </NavLink>
                  <NavLink end to="/admin/account/byType/all">
                    All Accounts
                  </NavLink>
                </div>
              )}
              <table className="account-list">
                <thead>
                <tr>
                  <td>Ref</td>
                  <td>Type</td>
                  <td>Name</td>
                  <td>Website</td>
                  <td>Edit</td>
                </tr>
                </thead>
                <tbody>
                {rows.map(({ id, name, type, website }) => (
                  <tr key={id}>
                    <td>CC-{id}</td>
                    <td>{type}</td>
                    <td>{name}</td>
                    <td>{website}</td>
                    <td>
                      <Link to={`/admin/account/${id}`}>Edit</Link>&nbsp;&middot;&nbsp;
                      <Link to={`/admin/account/${id}/user`}>Users</Link>&nbsp;&middot;&nbsp;
                      <Link to={`/admin/account/${id}/deployment`}>Deployments</Link>&nbsp;&middot;&nbsp;
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </Fragment>
          )}
        </PaginatedList>
      </section>
    </PageLayout>
  );
}
